import { useEffect, useState, useCallback } from "react";
import { Copy, Check, Download, Info, AppWindow } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton, SecondaryButton } from "../../components/Button/Buttons";
import { FormLabel } from "../../components/Labels/labels";
import Input from "../../components/Inputs/Input";
import Select from "../../components/Inputs/Select";
import MultiSelect from "../../components/Inputs/MultiSelect";
import Loader from "../../components/Loader/Loader";
import EmptyState from "../../components/shared/EmptyState";
import ViewToggle from "../../components/shared/ViewToggle";
import TileCard from "../../components/shared/TileCard";
import {
  listAdminClients,
  registerAdminClient,
} from "../../api/adminClients";
import { listAdminGroups } from "../../api/adminGroups";
import UserAuthStore from "../../store/userStore";
import { AuthError } from "../../api";
import ROUTES from "../../router/routes";
import useFeedback from "../../hooks/useFeedback";
import logger from "../../services/Logger";

// ─── Status badge ─────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  active:  { badge: "bg-fill-green-100 text-ink-green-800 dark:bg-fill-green-600/20 dark:text-ink-green-300", dot: "bg-fill-green-600",  label: "Active"  },
  revoked: { badge: "bg-fill-red-100 text-ink-red-800 dark:bg-fill-red-600/20 dark:text-ink-red-300", dot: "bg-fill-red-600",  label: "Revoked" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.active;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${s.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

// ─── Register App Form ────────────────────────────────────────────────────────
const RegisterForm = ({ onSuccess, onCancel }) => {
  const { showFeedback } = useFeedback();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [clientType, setClientType] = useState("");
  const [chatPlatform, setChatPlatform] = useState("");
  const [scopes, setScopes] = useState(["llm.chat"]);
  const [ownerTeam, setOwnerTeam] = useState("");
  const [ownerContact, setOwnerContact] = useState("");
  const [docsUrl, setDocsUrl] = useState("");
  const [allowedDomains, setAllowedDomains] = useState("");
  const [allowedChannels, setAllowedChannels] = useState("");
  const [allowedWorkspaces, setAllowedWorkspaces] = useState("");
  const [allowExternalSubjects, setAllowExternalSubjects] = useState(false);
  const [signingKeyPublic, setSigningKeyPublic] = useState("");
  const [webhookSigningSecret, setWebhookSigningSecret] = useState("");
  const [teamsBotAppId, setTeamsBotAppId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [groups, setGroups] = useState([]);
  const [defaultGroupId, setDefaultGroupId] = useState("");

  useEffect(() => {
    const orgId = UserAuthStore.getState().orgId;
    if (!orgId) return;
    listAdminGroups(orgId).then((data) => setGroups(data || [])).catch(() => {});
  }, []);

  const isDeviceType = clientType === "device" || clientType === "device_gateway";

  // Chat platform → auth method mapping (prevents invalid combinations)
  const CHAT_PLATFORM_AUTH = {
    slack:       "bot_token",
    teams:       "bot_token",
    webex:       "bot_token",
    google_chat: "signed_jwt",
    whatsapp:    "webhook_signature",
    telegram:    "webhook_signature",
    jabber:      "client_secret",
  };
  const derivedAuthMethod = chatPlatform ? (CHAT_PLATFORM_AUTH[chatPlatform] || "client_secret") : "client_secret";
  const showSigningSecret = clientType === "chat" && ["slack", "webex", "whatsapp", "telegram"].includes(chatPlatform);

  const CHAT_PLATFORMS = [
    { value: "slack",       label: "Slack",        description: "Slack Bot Token auth" },
    { value: "teams",       label: "Teams",        description: "Microsoft Teams Bot Token auth" },
    { value: "google_chat", label: "Google Chat",  description: "Google Chat — Signed JWT auth" },
    { value: "webex",       label: "Webex",        description: "Cisco Webex Bot Token auth" },
    { value: "whatsapp",    label: "WhatsApp",     description: "WhatsApp Cloud API — Webhook Signature auth" },
    { value: "telegram",    label: "Telegram",     description: "Telegram Bot — Webhook Signature auth" },
    { value: "jabber",      label: "Jabber / XMPP", description: "Jabber / XMPP — Client Secret auth" },
  ];

  const AVAILABLE_SCOPES = [
    { value: "llm.chat",           label: "llm.chat",           description: "Chat completions — access to /ai/chat with subject routing" },
    { value: "adk.agent.invoke",   label: "adk.agent.invoke",   description: "ADK agent invocation — access to /ai/agent" },
    { value: "media.image.generate", label: "media.image.generate", description: "Image generation — access to /ai/media/image/generate" },
    { value: "file.parse",         label: "file.parse",         description: "File text extraction — access to /files/parse" },
  ];



  const [newSecret, setNewSecret] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const _isDevice = clientType === "device" || clientType === "device_gateway";
    if (!name.trim() || !description.trim() || !clientType || scopes.length === 0) return;
    if (clientType === "chat" && !chatPlatform) return;
    if (_isDevice && !signingKeyPublic.trim()) return;
    if (chatPlatform === "teams" && !teamsBotAppId.trim()) {
      showFeedback("Teams Bot App ID is required.", "error");
      return;
    }
    if (showSigningSecret && !webhookSigningSecret.trim()) {
      showFeedback("Webhook signing secret is required for this platform.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const splitCsv = (val) => val.trim() ? val.split(",").map((v) => v.trim()).filter(Boolean) : [];
      const result = await registerAdminClient({
        name: name.trim(),
        description: description.trim(),
        client_type: clientType,
        client_auth_method: derivedAuthMethod,
        ...(clientType === "chat" && chatPlatform && { chat_platform: chatPlatform }),
        ...(_isDevice && { signing_key_public: signingKeyPublic.trim() }),
        ...(ownerTeam.trim() && { owner_team: ownerTeam.trim() }),
        ...(ownerContact.trim() && { owner_contact: ownerContact.trim() }),
        ...(docsUrl.trim() && { docs_url: docsUrl.trim() }),
        ...(defaultGroupId && { default_group_id: defaultGroupId }),
        policy_prism: {
          scopes: scopes,
          allowed_subject_domains: allowedDomains.trim()
            ? allowedDomains.split(",").map((d) => d.trim()).filter(Boolean)
            : [],
          allow_external_subjects: allowExternalSubjects,
          ...(clientType === "chat" && {
            allowed_channels:   splitCsv(allowedChannels).length   ? splitCsv(allowedChannels)   : null,
            allowed_workspaces: splitCsv(allowedWorkspaces).length ? splitCsv(allowedWorkspaces) : null,
          }),
          ...(showSigningSecret && { webhook_signing_secret: webhookSigningSecret.trim() }),
          ...(chatPlatform === "teams" && { teams_bot_app_id: teamsBotAppId.trim() }),
        },
      });
      setNewSecret(result);
    } catch {
      showFeedback("Failed to register app. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const [copiedId, setCopiedId] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const handleCopy = (text, setter) => {
    navigator.clipboard.writeText(text).then(() => {
      setter(true);
      setTimeout(() => setter(false), 2000);
    }).catch((err) => {
      // Previously unhandled — a failed clipboard write (e.g. insecure context,
      // denied permission) silently never showed the "copied" checkmark, with
      // no indication anything went wrong and an unhandled promise rejection.
      logger.error("AppsPage", "Failed to copy to clipboard", { error: err.message });
      showFeedback("Failed to copy to clipboard.", "error");
    });
  };

  const handleDownload = () => {
    const content = `Client ID: ${newSecret.client_id}\nClient Secret: ${newSecret.client_secret}\n\nStore these securely. The client secret will not be shown again.`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${newSecret.client_id}-credentials.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (newSecret) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-ink-green-600 dark:text-ink-green-300 font-medium">
          App registered successfully. Save your client secret — it will not be shown again.
        </p>
        <div className="space-y-2">
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">Client ID</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 block text-xs bg-surface-muted-raised dark:bg-surface-muted-raised rounded px-3 py-2 break-all text-ink-ink dark:text-ink-gray-200">
              {newSecret.client_id}
            </code>
            <button
              type="button"
              onClick={() => handleCopy(newSecret.client_id, setCopiedId)}
              title="Copy Client ID"
              className="flex-shrink-0 p-2 rounded-lg text-ink-gray-500 hover:text-ink-gradient-primary hover:bg-fill-gray-100 dark:hover:bg-fill-dialog-border transition-colors"
            >
              {copiedId ? <Check className="w-4 h-4 text-ink-green-600" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">Client Secret</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 block text-xs bg-fill-hex-fef9c3 dark:bg-fill-yellow-600/20 rounded px-3 py-2 break-all text-ink-ink dark:text-ink-hex-fde047">
              {newSecret.client_secret}
            </code>
            <button
              type="button"
              onClick={() => handleCopy(newSecret.client_secret, setCopiedSecret)}
              title="Copy Client Secret"
              className="flex-shrink-0 p-2 rounded-lg text-ink-gray-500 hover:text-ink-gradient-primary hover:bg-fill-gray-100 dark:hover:bg-fill-dialog-border transition-colors"
            >
              {copiedSecret ? <Check className="w-4 h-4 text-ink-green-600" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3 pt-1">
          <PrimaryButton onClick={onSuccess}>Done</PrimaryButton>
          <button
            type="button"
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl border border-edge-gray-200 dark:border-edge-gray-700 text-ink-gray-700 dark:text-ink-gray-300 hover:border-edge-gradient-primary hover:text-ink-gradient-primary transition-colors"
          >
            <Download className="w-4 h-4" />
            Download credentials
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <FormLabel>App Name</FormLabel>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My Integration" />
      </div>
      <div>
        <FormLabel>Description</FormLabel>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this app does" />
      </div>
      <div>
        <FormLabel>App Type</FormLabel>
        <Select
          value={clientType}
          onChange={(val) => setClientType(val)}
          placeholder="— Select an app type —"
          className="mt-1"
          options={[
            { value: "app",            label: "App",            description: "A backend server or service integration" },
            { value: "chat",           label: "Chat",           description: "A conversational interface or chatbot" },
            { value: "mobile",         label: "Mobile",         description: "An iOS or Android client application" },
            { value: "api",            label: "API",            description: "A direct machine-to-machine API consumer — supports human and service subjects" },
            { value: "automation",     label: "Automation",     description: "A workflow, script, or scheduled task — supports human and service subjects" },
            { value: "device_gateway", label: "Device Gateway", description: "A gateway that proxies requests on behalf of multiple devices" },
            { value: "device",         label: "Device",         description: "An individual device — requires a signing key pair for subject assertion" },
          ]}
        />
        {clientType && (
          <div className="mt-2 flex items-start gap-2 text-xs text-ink-gray-500 dark:text-ink-gray-400 bg-surface-muted dark:bg-surface-muted rounded-lg px-3 py-2">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              {clientType === "app" && "Use this for server-side integrations that call Photon AI on behalf of your users. The client secret is stored on your server and never exposed client-side."}
              {clientType === "chat" && "Use this for conversational interfaces (e.g. a support bot or embedded chat widget) that route messages through Photon AI."}
              {clientType === "mobile" && "Use this for iOS or Android apps. Each mobile install authenticates via a subject token — the client secret is exchanged server-side only."}
              {clientType === "api" && "Use this for direct machine-to-machine calls. Supports both human subjects (via invite or link) and service subjects (server-side callers that omit a human identity). Service subjects send X-Initiator-Type: schedule or system."}
              {clientType === "automation" && "Use this for scripts, cron jobs, or workflow tools (e.g. Zapier, n8n). Supports both human subjects and service subjects. Service subjects send X-Initiator-Type: event or schedule."}
              {clientType === "device_gateway" && "Use this for a server that proxies AI calls on behalf of multiple physical devices. Each device is identified by X-Client-Subject-ID. The gateway signs assertions with the registered public key (RS256)."}
              {clientType === "device" && "Use this for a single device (embedded system, sensor, edge node). The device signs each request with its private key; Photon verifies against the registered public key (RS256)."}
            </span>
          </div>
        )}
      </div>

      {clientType === "chat" && (
        <>
          <div>
            <FormLabel>Chat Platform *</FormLabel>
            <Select
              value={chatPlatform}
              onChange={(val) => setChatPlatform(val)}
              placeholder="— Select a platform —"
              className="mt-1"
              options={CHAT_PLATFORMS}
            />
          </div>
          {chatPlatform && (
            <div className="flex items-start gap-2 text-xs text-ink-gray-500 dark:text-ink-gray-400 bg-surface-muted dark:bg-surface-muted rounded-lg px-3 py-2">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Auth method: <span className="font-medium text-ink-gray-700 dark:text-ink-gray-300 font-mono">{derivedAuthMethod}</span> — determined automatically by platform.</span>
            </div>
          )}
          {showSigningSecret && (
            <div>
              <FormLabel>Webhook Signing Secret *</FormLabel>
              <Input
                type="password"
                value={webhookSigningSecret}
                onChange={(e) => setWebhookSigningSecret(e.target.value)}
                placeholder="Paste the signing secret from your platform dashboard"
              />
              <p className="mt-1 text-xs text-ink-gray-400">
                Required for platform signature verification. Stored encrypted — cannot be retrieved after saving.
              </p>
            </div>
          )}
          {chatPlatform === "teams" && (
            <div>
              <FormLabel>Teams Bot App ID *</FormLabel>
              <Input
                value={teamsBotAppId}
                onChange={(e) => setTeamsBotAppId(e.target.value)}
                placeholder="Microsoft App ID from your bot registration"
              />
              <p className="mt-1 text-xs text-ink-gray-400">
                Used to verify that Microsoft issued each request token for this bot.
              </p>
            </div>
          )}
          <div>
            <FormLabel>Allowed Channels <span className="text-ink-gray-400 font-normal">(optional)</span></FormLabel>
            <Input
              value={allowedChannels}
              onChange={(e) => setAllowedChannels(e.target.value)}
              placeholder="e.g. C1234567890, general — leave blank to allow all channels"
            />
            <p className="mt-1 text-xs text-ink-gray-400">Comma-separated channel IDs or names. Leave blank to allow any channel.</p>
          </div>
          <div>
            <FormLabel>Allowed Workspaces <span className="text-ink-gray-400 font-normal">(optional)</span></FormLabel>
            <Input
              value={allowedWorkspaces}
              onChange={(e) => setAllowedWorkspaces(e.target.value)}
              placeholder="e.g. T1234567890, myworkspace — leave blank to allow all workspaces"
            />
            <p className="mt-1 text-xs text-ink-gray-400">Comma-separated workspace IDs or names. Leave blank to allow any workspace.</p>
          </div>
        </>
      )}
      {isDeviceType && (
        <div>
          <FormLabel>Signing Key (Public) *</FormLabel>
          <textarea
            rows={6}
            value={signingKeyPublic}
            onChange={(e) => setSigningKeyPublic(e.target.value)}
            placeholder={"-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----"}
            className="photon-field w-full font-mono text-xs rounded-lg border border-edge-gray-200 dark:border-edge-panel-border bg-fill-white dark:bg-fill-deep-surface text-ink-ink dark:text-ink-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-focus-primary/50 resize-y"
          />
          <p className="mt-1 text-xs text-ink-gray-400">
            PEM-encoded RSA public key. Devices sign each request's <code>X-Subject-Assertion</code> JWT with the corresponding private key.
          </p>
        </div>
      )}

      <div>
        <FormLabel>Scopes</FormLabel>
        <MultiSelect
          value={scopes}
          onChange={setScopes}
          options={AVAILABLE_SCOPES}
          placeholder="— Select scopes —"
          className="mt-1"
        />
      </div>
      <div>
        <FormLabel>Owner Team <span className="text-ink-gray-400 font-normal">(optional)</span></FormLabel>
        <Input value={ownerTeam} onChange={(e) => setOwnerTeam(e.target.value)} placeholder="e.g. Platform Engineering" />
      </div>
      <div>
        <FormLabel>Owner Contact <span className="text-ink-gray-400 font-normal">(optional)</span></FormLabel>
        <Input value={ownerContact} onChange={(e) => setOwnerContact(e.target.value)} placeholder="e.g. owner@example.com" />
      </div>
      <div>
        <FormLabel>Docs URL <span className="text-ink-gray-400 font-normal">(optional)</span></FormLabel>
        <Input value={docsUrl} onChange={(e) => setDocsUrl(e.target.value)} placeholder="https://..." />
      </div>
      <div>
        <FormLabel>Allowed Subject Domains <span className="text-ink-gray-400 font-normal">(optional)</span></FormLabel>
        <Input
          value={allowedDomains}
          onChange={(e) => setAllowedDomains(e.target.value)}
          placeholder="e.g. example.com, partner.org — leave blank for all domains"
        />
        <p className="mt-1 text-xs text-ink-gray-400">Comma-separated. Leave blank to allow subjects from any domain.</p>
      </div>
      <div className="flex items-center justify-between py-2">
        <div>
          <FormLabel className="mb-0">Allow External Subjects</FormLabel>
          <p className="text-xs text-ink-gray-400 mt-0.5">Permit users outside your organisation to authenticate as subjects via invite links.</p>
        </div>
        <button
          type="button"
          onClick={() => setAllowExternalSubjects((v) => !v)}
          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
            allowExternalSubjects ? "bg-fill-primary" : "bg-fill-gray-300 dark:bg-fill-gray-600"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-fill-white shadow transition duration-200 ${
              allowExternalSubjects ? "translate-x-4" : "translate-x-0"
            }`}
          />
        </button>
      </div>
      {groups.filter((g) => g.source === "photon_native").length > 0 && (
        <div>
          <FormLabel>Default Group <span className="text-ink-gray-400 font-normal">(optional)</span></FormLabel>
          <Select
            value={defaultGroupId}
            onChange={setDefaultGroupId}
            placeholder="— No default group —"
            options={[
              { value: "", label: "— No default group —" },
              ...groups.filter((g) => g.source === "photon_native").map((g) => ({
                value: g.group_id,
                label: g.display_name || g.group_id,
              })),
            ]}
          />
          <p className="mt-1 text-xs text-ink-gray-400">New subjects connecting through this app will be auto-assigned to this group.</p>
        </div>
      )}
      <div className="flex gap-3 pt-2">
        <PrimaryButton type="submit" disabled={submitting || !name.trim() || !description.trim() || !clientType || scopes.length === 0 || (clientType === "chat" && !chatPlatform) || (isDeviceType && !signingKeyPublic.trim())}>
          {submitting ? "Registering…" : "Register App"}
        </PrimaryButton>
        <SecondaryButton type="button" onClick={onCancel}>Cancel</SecondaryButton>
      </div>
    </form>
  );
};

// ─── App Avatar ───────────────────────────────────────────────────────────────
const AppAvatar = ({ name }) => (
  <div className="photon-avatar w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0">
    <span className="text-xs font-semibold">
      {(name || "A")[0].toUpperCase()}
    </span>
  </div>
);

// ─── App Tile (grid view) ─────────────────────────────────────────────────────
const AppTile = ({ client, onClick, onEdit, onDelete }) => {
  const backContent = (
    <div className="flex flex-col gap-1.5 pr-6">
      <h4 className="text-xs font-semibold text-ink-ink dark:text-ink-white mb-1">App Details</h4>
      <p className="text-[10px] text-ink-gray-500 dark:text-ink-gray-400">
        <span className="font-medium text-ink-gray-700 dark:text-ink-gray-300">Client ID: </span>
        <span className="font-mono break-all">{client.client_id}</span>
      </p>
      {client.owner_team && (
        <p className="text-[10px] text-ink-gray-500 dark:text-ink-gray-400">
          <span className="font-medium text-ink-gray-700 dark:text-ink-gray-300">Team: </span>
          {client.owner_team}
        </p>
      )}
      {client.owner_contact && (
        <p className="text-[10px] text-ink-gray-500 dark:text-ink-gray-400">
          <span className="font-medium text-ink-gray-700 dark:text-ink-gray-300">Contact: </span>
          {client.owner_contact}
        </p>
      )}
      {client.policy_prism?.scopes?.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {client.policy_prism.scopes.map((s) => (
            <span key={s} className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono bg-fill-gray-100 dark:bg-fill-dialog-border text-ink-gray-500 dark:text-ink-gray-400">
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <motion.div layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
      <TileCard
        avatar={<AppAvatar name={client.name} />}
        title={client.name}
        tag={client.client_type}
        description={client.description || undefined}
        active={!client.status || client.status === "active"}
        status={client.status === "revoked" ? (
          <span className="block w-2.5 h-2.5 rounded-full bg-fill-red-500 ring-2 ring-focus-white dark:ring-focus-dialog-surface" title="Revoked" />
        ) : undefined}
        onCardClick={onClick}
        backContent={backContent}
      />
    </motion.div>
  );
};

// ─── App Row (list view) ──────────────────────────────────────────────────────
const AppRow = ({ client, onClick }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6 }}
    onClick={onClick}
    className="flex items-center justify-between px-4 py-3 rounded-xl bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-panel-border cursor-pointer hover:border-edge-primary dark:hover:border-edge-primary transition-colors"
  >
    <div className="flex items-center gap-3 min-w-0">
      <AppAvatar name={client.name} />
      <div className="min-w-0">
        <p className="text-sm font-medium text-ink-ink dark:text-ink-gray-200 truncate">{client.name}</p>
        <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 truncate font-mono">{client.client_id}</p>
      </div>
    </div>
    <div className="flex items-center gap-3 flex-shrink-0">
      <span className="hidden sm:block text-xs text-ink-gray-500 dark:text-ink-gray-400">{client.client_type}</span>
      <StatusBadge status={client.status || "active"} />
    </div>
  </motion.div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const AppsPage = () => {
  const { showFeedback } = useFeedback();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAdminClients();
      setClients(data || []);
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load apps.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleRegistered = () => {
    setShowRegister(false);
    fetchClients();
  };

  return (
    <SettingsPageShell
      title="Apps"
      description="Manage client integrations that allow external applications to connect to Photon AI using OAuth 2.0 client credentials and API access."
      action={!loading && clients.length > 0 ? { label: "Register App", onClick: () => setShowRegister(true) } : undefined}
    >
      {/* Toolbar — view toggle, only when apps are loaded and present */}
      {!loading && clients.length > 0 && (
        <div className="flex items-center justify-end mb-4">
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
      )}

      {/* Register form */}
      <AnimatePresence>
        {showRegister && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto", transitionEnd: { overflow: "visible" } }}
            exit={{ opacity: 0, height: 0, overflow: "hidden" }}
            style={{ overflow: "hidden" }}
            className="mb-6"
          >
            <div className="p-4 rounded-xl border border-edge-gray-200 dark:border-edge-panel-border bg-surface-sidebar dark:bg-surface-sidebar">
              <p className="text-sm font-semibold text-ink-ink dark:text-ink-gray-200 mb-4">Register New App</p>
              <RegisterForm onSuccess={handleRegistered} onCancel={() => setShowRegister(false)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      {loading ? (
        <Loader />
      ) : clients.length === 0 && !showRegister ? (
        <EmptyState
          icon={<AppWindow className="w-7 h-7 text-ink-primary dark:text-ink-accent" />}
          title="No apps registered yet"
          description="Register your first app to start connecting external services to Photon AI."
          action={{ label: "Register App", onClick: () => setShowRegister(true) }}
        />
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {clients.map((client) => (
              <AppTile
                key={client.client_id}
                client={client}
                onClick={() => navigate(ROUTES.SETTINGS.APPS_DETAIL(client.client_id))}
                onDelete={() => {/* TODO: delete handler */}}
              />
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div className="space-y-2">
          <AnimatePresence>
            {clients.map((client) => (
              <AppRow
                key={client.client_id}
                client={client}
                onClick={() => navigate(ROUTES.SETTINGS.APPS_DETAIL(client.client_id))}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </SettingsPageShell>
  );
};

export default AppsPage;
